import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { UserFeedbackInsert, UserFeedback } from "@/integrations/supabase/types";
import { toast } from "@/hooks/use-toast";

export function useSubmitFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedback: UserFeedbackInsert) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { data, error } = await supabase
        .from("user_feedback" as any)
        .insert({
          user_id: user.id,
          type: feedback.type,
          title: feedback.title,
          description: feedback.description || null,
          page_url: feedback.page_url || window.location.pathname,
          user_agent: feedback.user_agent || navigator.userAgent,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: "Feedback enviado com sucesso! Obrigado pela sua contribuição." });
      queryClient.invalidateQueries({ queryKey: ["user-feedback"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao enviar feedback", variant: "destructive" });
    },
  });
}

export function useAddFeedbackMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ feedbackId, message }: { feedbackId: string; message: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Fetch current description to append
      const { data: current, error: fetchError } = await supabase
        .from("user_feedback" as any)
        .select("description")
        .eq("id", feedbackId)
        .eq("user_id", user.id)
        .single();

      if (fetchError) throw fetchError;

      const timestamp = new Date().toLocaleString("pt-BR");
      const separator = "\n\n---\n";
      const newMessage = `[${timestamp}] ${message}`;
      const updatedDescription = current?.description
        ? `${(current as any).description}${separator}${newMessage}`
        : newMessage;

      const { data, error } = await supabase
        .from("user_feedback" as any)
        .update({ description: updatedDescription })
        .eq("id", feedbackId)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: "Mensagem adicionada ao feedback" });
      queryClient.invalidateQueries({ queryKey: ["user-feedback"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao adicionar mensagem", variant: "destructive" });
    },
  });
}

export function useUserFeedback() {
  return useQuery<UserFeedback[]>({
    queryKey: ["user-feedback"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { data, error } = await supabase
        .from("user_feedback" as any)
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as unknown as UserFeedback[];
    },
    staleTime: 60 * 1000,
  });
}
